// Wind streamlines — pencil strokes riding the interpolated 10 m wind. Sparse and slow, Windy-style: from far
// out a few long streamlines show the flow through the islands; at beach zooms the strokes are shorter and are
// steered by the terrain under them (a heuristic drawn from the elevation model, not a wind model): slowed
// over land, bent round slopes, calmed and stirred in the lee of ridges, squeezed faster through gaps.
export class WindLayer {
  constructor(map, canvas) {
    this.map = map; this.canvas = canvas; this.ctx = canvas.getContext('2d');
    this.field = null; this.step = 0; this.enabled = true; this.terrainAware = true; this.density = 1;
    this.particles = []; this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.resize(); this._last = performance.now(); this._clear = false;
    map.on('move', () => { this._clear = true; });
    map.on('resize', () => this.resize());
  }
  resize() {
    const r = this.map.getContainer().getBoundingClientRect(); this.w = r.width; this.h = r.height;
    this.canvas.width = Math.round(this.w * this.dpr); this.canvas.height = Math.round(this.h * this.dpr);
    this.canvas.style.width = this.w + 'px'; this.canvas.style.height = this.h + 'px';
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0); this._clear = true;
  }
  setField(f) { this.field = f; this.particles.length = 0; this._clear = true; }
  setStep(s) { this.step = s; this._clear = true; }
  setEnabled(on) { this.enabled = on; if (!on) this.ctx.clearRect(0, 0, this.w, this.h); else { this.particles.length = 0; this._clear = true; } }
  mpp() { const z = this.map.getZoom(), lat = this.map.getCenter().lat; return 156543.03 * Math.cos(lat * Math.PI / 180) / Math.pow(2, z); }
  bounds() { const b = this.map.getBounds(); return { w: b.getWest(), e: b.getEast(), s: b.getSouth(), n: b.getNorth() }; }
  spawn(p, b) {
    const pad = 0.2, lonSpan = b.e - b.w, latSpan = b.n - b.s;
    p.lon = b.w - lonSpan * pad + Math.random() * lonSpan * (1 + 2 * pad);
    p.lat = b.s - latSpan * pad + Math.random() * latSpan * (1 + 2 * pad);
    p.age = 60 + Math.random() * 160; p.x = null; p.tf = null; p.tdist = 0; p.wob = Math.random() * Math.PI * 2;
    return p;
  }
  elev(lon, lat) { try { const e = this.map.queryTerrainElevation({ lng: lon, lat }); return e == null || Number.isNaN(e) ? 0 : e; } catch { return 0; } }
  terrainFactor(lon, lat, dirRad) {
    const d = 160, dlat = 1 / 110540, dlon = 1 / (111320 * Math.cos(lat * Math.PI / 180));
    const ax = Math.sin(dirRad), ay = Math.cos(dirRad), lx = -ay, ly = ax;
    const h0 = this.elev(lon, lat), hA = this.elev(lon + ax * d * dlon, lat + ay * d * dlat);
    const hL = this.elev(lon + lx * d * dlon, lat + ly * d * dlat), hR = this.elev(lon - lx * d * dlon, lat - ly * d * dlat);
    let mult = 1, steer = 0, jitter = 0;
    if (h0 > 3) mult *= 0.7;
    const slope = (hA - h0) / d;
    if (slope > 0.04) { mult *= Math.max(0.3, 1 - slope * 3.5); steer = (hL < hR ? -1 : 1) * Math.min(0.9, slope * 5); }
    if (hL > h0 + 50 && hR > h0 + 50) mult *= 1.3;
    let hmax = h0; for (const s of [300, 600, 900]) hmax = Math.max(hmax, this.elev(lon - ax * s * dlon, lat - ay * s * dlat));
    const shelter = hmax - h0;
    if (shelter > 60) { mult *= Math.max(0.25, 1 - shelter / 400); jitter = Math.min(0.6, shelter / 500); }
    return { mult, steer, jitter };
  }
  // graphite: light, then heavier and warmer with wind strength
  colour(kt) {
    if (kt < 6) return 'rgba(96,104,112,0.35)';
    if (kt < 12) return 'rgba(80,86,94,0.55)';
    if (kt < 18) return 'rgba(70,74,82,0.7)';
    if (kt < 25) return 'rgba(150,80,40,0.8)';
    return 'rgba(178,52,40,0.9)';
  }
  frame(now) {
    if (!this.enabled || !this.field) return;
    const ctx = this.ctx, w = this.w, h = this.h;
    if (this._clear) { ctx.clearRect(0, 0, w, h); this._clear = false; for (const p of this.particles) p.x = null; }
    const z = this.map.getZoom(), pitched = this.map.getPitch() > 5;
    // sparse: a few hundred strokes on a laptop screen, fewer still zoomed out
    const target = Math.round(Math.min(900, Math.max(160, (w * h) / (z < 10 ? 9000 : z < 12 ? 6500 : 4800))) * this.density);
    const b = this.bounds();
    while (this.particles.length < target) this.particles.push(this.spawn({}, b));
    if (this.particles.length > target) this.particles.length = target;
    ctx.globalCompositeOperation = 'destination-in';
    ctx.fillStyle = `rgba(0,0,0,${z < 10 ? 0.975 : 0.965})`;   // long, slow-fading trails
    ctx.fillRect(0, 0, w, h);
    ctx.globalCompositeOperation = 'source-over';
    ctx.lineWidth = z < 10 ? 1.0 : z < 12.5 ? 1.2 : 1.5; ctx.lineCap = 'round';
    const mpp = this.mpp(), terrain = this.terrainAware && z >= 11.3;
    const dtF = Math.min(2, (now - this._last) / 16.7); this._last = now;
    const t = this.step, dlat = 1 / 110540;
    for (const p of this.particles) {
      if (p.age <= 0 || p.lon < b.w - 0.25 || p.lon > b.e + 0.25 || p.lat < b.s - 0.25 || p.lat > b.n + 0.25) this.spawn(p, b);
      const v = this.field.vector('ws', 'wd', p.lon, p.lat, t);
      let kt = v.speed, dir = Math.atan2(v.u, v.v);
      if (terrain) {
        if (!p.tf || p.tdist > 120) { p.tf = this.terrainFactor(p.lon, p.lat, dir); p.tdist = 0; }
        kt *= p.tf.mult; dir += p.tf.steer * 0.35 + (p.tf.jitter ? (Math.random() - 0.5) * p.tf.jitter : 0);
      }
      // a slow pencil hand: ~1 px per frame at 15 kt, with a gentle wobble so strokes don't read as rulers
      p.wob += 0.05 * dtF; dir += Math.sin(p.wob) * 0.04;
      const px = Math.min(2.2, Math.max(0.2, kt * 0.075)) * dtF;
      const m = px * mpp; p.tdist += m;
      const dlon = 1 / (111320 * Math.cos(p.lat * Math.PI / 180));
      const nlon = p.lon + Math.sin(dir) * m * dlon, nlat = p.lat + Math.cos(dir) * m * dlat;
      const s = this.map.project([nlon, nlat]);
      if (p.x != null && Math.abs(s.x - p.x) < 40 && Math.abs(s.y - p.y) < 40) {
        ctx.strokeStyle = this.colour(kt); ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(s.x, s.y); ctx.stroke();
      }
      p.x = s.x; p.y = s.y; p.lon = nlon; p.lat = nlat; p.age -= dtF;
      if (pitched && (s.y < -50 || s.y > h + 50 || s.x < -50 || s.x > w + 50)) p.age = 0;
    }
  }
}
